import { useSignIn } from "@clerk/clerk-expo";
import { useToken } from "@gluestack-style/react";
import OAuthSignIn from "components/clerk/OAuthSignIn";
import { Box, Button, HStack, Image, Input, InputField, Paragraph, ParagraphSmall, Spinner, Typography } from "components/ui";
import { useRouter } from "expo-router";
import * as WebBrowser from "expo-web-browser";
import { ChevronRight, TriangleAlert } from "lucide-react-native";
import React, { useState } from "react";
import { Platform, View } from "react-native";
import abcoLogo from "../assets/logo.svg";

interface SignInProps {
	setIsSignUp: (isSignUp: boolean) => void;
}

export const useWarmUpBrowser = () => {
	React.useEffect(() => {
		void WebBrowser.warmUpAsync();
		return () => {
			void WebBrowser.coolDownAsync();
		};
	}, []);
};

if (Platform.OS !== "web") {
	WebBrowser.maybeCompleteAuthSession();
}

const SignIn = ({ setIsSignUp }: any) => {
	if (Platform.OS !== "web") {
		useWarmUpBrowser();
	}

	const { signIn, setActive, isLoaded } = useSignIn();
	const router = useRouter();

	const [emailAddress, setEmailAddress] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const resolvedRed = useToken("colors", "errormain");

	const isValidEmail = (email: string) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(String(email).toLowerCase());
	};
	const handleEmailSubmit = async () => {
		if (!isLoaded || !signIn) {
			return;
		}
		if (!isValidEmail(emailAddress)) {
			setEmailError("Please enter a valid email address");
			return;
		}
		setEmailError("");
		setIsLoading(true);

		try {
			const signInAttempt = await signIn.create({
				identifier: emailAddress,
			});
			if (signInAttempt.supportedFirstFactors && signInAttempt.supportedFirstFactors.find((factor) => factor.strategy === "password")) {
				setShowPassword(true);
			}
		} catch (err: any) {
			if (err.errors?.[0]?.code === "form_identifier_not_found") {
				setEmailError("Account not found. Please sign up.");
			} else {
				setEmailError("An error occurred. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};

	const handlePasswordSubmit = async () => {
		if (!isLoaded || !signIn) {
			return;
		}
		if (!password) {
			setPasswordError("Please enter your password");
			return;
		}
		setPasswordError("");
		setIsLoading(true);

		try {
			const signInAttempt = await signIn.create({
				identifier: emailAddress,
				password,
			});

			if (signInAttempt.status === "complete") {
				await setActive({ session: signInAttempt.createdSessionId });
				router.replace("/");
			}
		} catch (err: any) {
			if (err.errors?.[0]?.code === "form_password_incorrect") {
				setPasswordError("Incorrect password");
			} else {
				setPasswordError("Login failed. Please try again.");
			}
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<Box my={60}>
			<Box
				sx={{
					shadowColor: "#000",
					shadowOffset: {
						width: 0,
						height: 1,
					},
					shadowOpacity: 0.18,
					shadowRadius: 1.0,
					elevation: 1,
				}}
				h={"$full"}
				maxWidth={450}
				w={"$full"}
				borderRadius={12}
				paddingHorizontal={40}
				paddingVertical={32}
				marginInline="auto"
				bg="$neutral50"
				alignSelf="center"
			>
				<Box w={"$full"}>
					<Box gap={24} alignItems="center" justifyContent="center" mb={24}>
						<Box maxWidth={120}>
							<Image resizeMode="contain" source={abcoLogo} w={172} h={44} alt="Abco: The Power to Clean 8" style={{ width: 172, height: 44 }} />
						</Box>
						<Box alignItems="center">
							<Typography.H4>Sign in to Abco</Typography.H4>
							<ParagraphSmall sx={{ color: "$neutral600" }}>Welcome back! Please sign in to continue.</ParagraphSmall>
						</Box>
					</Box>
					<Box>
						<OAuthSignIn layout="grid" />
					</Box>
					<Box gap={12} flexDirection="row" alignItems="center" width="100%" my="$5" mx="auto" px="$5">
						<Box flex={1} height="$px" bg="$neutral300" />
						<ParagraphSmall>or</ParagraphSmall>
						<Box flex={1} height="$px" bg="$neutral300" />
					</Box>
					<View>
						<Box>
							<Input sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
								<InputField
									autoCapitalize="none"
									value={emailAddress}
									placeholder="Enter your email address"
									onChangeText={(text) => {
										setEmailAddress(text);
										setEmailError("");
									}}
									onSubmitEditing={handleEmailSubmit}
									editable={!isLoading}
								/>
							</Input>
							{emailError && (
								<HStack width={"$full"} mt={24} padding={6} borderRadius={4} gap={8} alignItems="center" justifyContent="center" bg="$errorlight">
									<TriangleAlert color={resolvedRed} size={16} />
									<ParagraphSmall sx={{ color: "$errormain" }}>{emailError}</ParagraphSmall>
								</HStack>
							)}

							{showPassword && (
								<Input mt={24} sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
									<InputField
										value={password}
										placeholder="Enter your password"
										secureTextEntry={true}
										onChangeText={(text) => {
											setPassword(text);
											setPasswordError("");
										}}
										onSubmitEditing={handlePasswordSubmit}
										editable={!isLoading}
									/>
								</Input>
							)}

							{passwordError && (
								<HStack width={"$full"} mt={24} padding={6} borderRadius={4} gap={8} alignItems="center" justifyContent="center" bg="$errorlight">
									<TriangleAlert color={resolvedRed} size={16} />
									<ParagraphSmall sx={{ color: "$errormain" }}>{passwordError}</ParagraphSmall>
								</HStack>
							)}

							<Button
								variant="primary"
								gap={12}
								alignItems="center"
								borderRadius={8}
								borderWidth={1}
								paddingVertical={6}
								mt={24}
								borderColor="$primarylight"
								onPress={showPassword ? handlePasswordSubmit : handleEmailSubmit}
								disabled={isLoading}
							>
								<Paragraph sx={{ color: "white" }}>{isLoading ? <Spinner color="$basicwhite" /> : showPassword ? "Sign In" : "Continue"}</Paragraph>
								{!isLoading && <ChevronRight size={16} color="white" />}
							</Button>
						</Box>
					</View>
					<HStack justifyContent="center" alignItems="center" mt={24}>
						<ParagraphSmall>Don't have an account?</ParagraphSmall>
						{/* <ParagraphSmall>
							Don't have an account?
							<Link href="login?sign-up=true" style={{ color: "#0066cc" }}>
								Sign up
							</Link>
						</ParagraphSmall> */}
						<Button sx={{ paddingHorizontal: 6 }} onPress={() => setIsSignUp(true)}>
							<ParagraphSmall sx={{ color: "#0066cc" }}>Sign up</ParagraphSmall>
						</Button>
					</HStack>
				</Box>
			</Box>
		</Box>
	);
};
export default SignIn;
